import { StateCreator } from 'zustand';
import { IAppState, IRecommendedVehiclesSlice } from './stateTypes';
import { fetchAndBuildRecommendedVehicleTiles } from '../../pages/home/utilities/recommendationsBuilder';
import { IVehicleTile } from '../../types/vehicleTile';
import { ICarRecommendationsResponse } from '../../pages/home/car-recommendations/types';
import { mapCarRecommendationToVehicleTile } from '../../pages/home/car-recommendations/carRecommendationMapper';
import HomePageExperience from '../../pages/home/hero/store/HomePageExperience';

const UFT_VIEWED_VEHICLE_TARGET = 10;
const UFT_RECOMMENDATIONS_TARGET = 10;
const UFT_RECOMMENDATIONS_BUFFER = 3;
const UFT_RECOMMENDATIONS_MAX_TAKE = 20;

const createRecommendedVehiclesSlice: StateCreator<IAppState, [], [], IRecommendedVehiclesSlice> = (set, get) => ({
    recommendationsCohortId: '',
    setRecommendationsCohortId: (cohortId) => {
        set({ recommendationsCohortId: cohortId });
    },
    recommendationsTestId: '',
    setRecommendationsTestId: (testId) => {
        set({ recommendationsTestId: testId });
    },
    recommendedVehicles: [],
    isRecommendedVehiclesLoading: false,
    setRecommendedVehicles: async () => {
        set({ isRecommendedVehiclesLoading: true });
        await get().setZipCode();
        await get().setStoreId();

        // Get local vehicles
        let recommendedVehicles: IVehicleTile[] | undefined;

        const recommendedVehicleTiles = await fetchAndBuildRecommendedVehicleTiles();
        if (recommendedVehicleTiles.vehicleTiles.length > 0) {
            recommendedVehicles = recommendedVehicleTiles.vehicleTiles;
        }

        // Include FBS data
        if (recommendedVehicles && recommendedVehicles?.length > 0) {
            await get().getFbsDataForStockNumbers(recommendedVehicles.map(({ stockNumber }) => stockNumber));

            recommendedVehicles.forEach((vehicle: IVehicleTile) => {
                const decision = get().fbsData?.find((fbs) => fbs.stockNumber === vehicle.stockNumber);
                if (decision) {
                    vehicle.fbsData = decision;
                }
            });
        }

        // Update store
        set({ recommendedVehicles });
        set({ recommendationsCohortId: recommendedVehicleTiles.cohortId });
        set({ recommendationsTestId: recommendedVehicleTiles.testId });
        set({ isRecommendedVehiclesLoading: false });
    },
    uftRecommendedVehicles: [],
    uftRecommendedVehiclesRequestedTake: undefined,
    uftRecommendedVehiclesLoaded: false,
    uftRecommendedVehiclesLoading: false,
    getUftRecommendedVehicles: async () => {
        set({ uftRecommendedVehiclesLoading: true });

        await Promise.all([get().setVisitorId(), get().setZipCode(), get().setStoreId()]);

        const visitorId = get().visitorId;
        if (!visitorId) {
            set({
                uftRecommendedVehiclesLoading: false,
                uftRecommendedVehiclesLoaded: true,
            });
            return;
        }

        const type =
            get().experience === HomePageExperience.FirstTimeVisitor ? 'ready_today_near_you' : 'you_might_like';
        const recentlyViewedVehiclesCount = Math.min(get().recentlyViewedVehicles.length, UFT_VIEWED_VEHICLE_TARGET);
        const shouldTopUpFromRecommendations = get().optimizedReturnExperience === 'test';
        const topUpNeeded =
            shouldTopUpFromRecommendations && recentlyViewedVehiclesCount > 0
                ? Math.max(0, UFT_VIEWED_VEHICLE_TARGET - recentlyViewedVehiclesCount)
                : 0;
        const take = Math.min(
            UFT_RECOMMENDATIONS_TARGET + topUpNeeded + UFT_RECOMMENDATIONS_BUFFER,
            UFT_RECOMMENDATIONS_MAX_TAKE,
        );
        set({ uftRecommendedVehiclesRequestedTake: take });

        try {
            const params = new URLSearchParams({
                visitorId,
                type,
                pageType: 'Home',
                storeId: get().storeId || '',
                take: take.toString(),
            });
            const zipCode = get().zipCode;
            if (zipCode) params.set('zipCode', zipCode);

            const response = await fetch(`/home/api/car-recommendations?${params}`);

            if (response.status === 204) {
                // No recommendations available - this is expected, not an error
                set({
                    uftRecommendedVehiclesLoading: false,
                    uftRecommendedVehiclesLoaded: true,
                });
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: ICarRecommendationsResponse = await response.json();

            if (data?.items?.length > 0) {
                // CarTile expects imageLink, not heroImageUrl
                set({
                    uftRecommendedVehicles: data.items.map((item) => mapCarRecommendationToVehicleTile(item)),
                    displayText: data.displayText,
                    searchToken: data.searchToken,
                    uftRecommendedVehiclesLoading: false,
                    uftRecommendedVehiclesLoaded: true,
                });
            } else {
                set({
                    uftRecommendedVehiclesLoading: false,
                    uftRecommendedVehiclesLoaded: true,
                });
            }
        } catch (error) {
            console.error('Error fetching car recommendations:', error);
            set({
                uftRecommendedVehiclesLoading: false,
                uftRecommendedVehiclesLoaded: true,
            });
        }
    },
});

export default createRecommendedVehiclesSlice;
